import { useState, useRef, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from '@happymoneyinc/styled-components';
import { jsxs, jsx } from 'react/jsx-runtime';

const TextFieldSlider = _ref => {
  let {
    prompt,
    value,
    onValueChange,
    min,
    max,
    step,
    minLabel,
    maxLabel,
    id,
    onIsValidChange,
    showDollarSign
  } = _ref;
  const [minimumExceeded, setMinimumExceeded] = useState(false);
  const [maximumExceeded, setMaximumExceeded] = useState(false);
  const textInput = useRef(null);
  const formatTextInputValue = useCallback(value => {
    let textValue = `${parseInt(value).toLocaleString()}`;
    if (showDollarSign) textValue = `$${textValue}`;
    return textValue;
  }, [showDollarSign]);
  const [textInputValue, setTextInputValue] = useState(formatTextInputValue(value));
  const clearErrors = useCallback(() => {
    setMinimumExceeded(false);
    setMaximumExceeded(false);
    onIsValidChange(true);
  }, [onIsValidChange]);
  const removeNonNumeric = useCallback(value => {
    return value.split('.')[0].replace(/\D/g, '');
  }, []);

  /*
  Handler for:
  1) Dragging the slider
  2) Moving focus out of the text field
  3) Pressing the Return key with the text field focused
  */
  const handleChange = useCallback(_ref2 => {
    let {
      target
    } = _ref2;
    clearErrors();
    let updatedValue = removeNonNumeric(target.value);
    if (!updatedValue || updatedValue < min) {
      setMaximumExceeded(false);
      setMinimumExceeded(true);
      onIsValidChange(false);
      updatedValue = min;
    } else if (updatedValue > max) {
      setMinimumExceeded(false);
      setMaximumExceeded(true);
      onIsValidChange(false);
      updatedValue = max;
    }
    const textInputValue = formatTextInputValue(updatedValue);
    updatedValue = parseInt(updatedValue);
    setTextInputValue(textInputValue);
    onValueChange(updatedValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handler for keyboard edits to the value of the text input
  const handleInput = useCallback(_ref3 => {
    let {
      target
    } = _ref3;
    let inputValue = removeNonNumeric(target.value);
    let textInputValue;
    if (inputValue !== '') {
      textInputValue = formatTextInputValue(inputValue);
    } else {
      textInputValue = '';
    }
    if (inputValue < min) {
      onIsValidChange(false);
      setMaximumExceeded(false);
      setMinimumExceeded(true);
    } else if (inputValue > max) {
      onIsValidChange(false);
      setMinimumExceeded(false);
      setMaximumExceeded(true);
    } else {
      clearErrors();
    }
    const integerValue = parseInt(inputValue);
    inputValue = Number.isNaN(integerValue) ? min : integerValue;
    onValueChange(inputValue);
    setTextInputValue(textInputValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    const textInputRef = textInput.current;
    if (textInputRef) {
      textInputRef.onblur = handleChange;
      textInputRef.oninput = handleInput;
      textInputRef.onkeydown = event => {
        if (event.key === 'Enter') {
          handleChange(event);
        }
      };
    }
    return () => {
      textInputRef.onblur = null;
      textInputRef.oninput = null;
      textInputRef.onkeydown = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const noOp = useCallback(() => {}, []);
  const errorState = minimumExceeded || maximumExceeded;
  return /*#__PURE__*/jsxs(TextFieldSliderContainer, {
    id: id,
    children: [/*#__PURE__*/jsx(TextFieldSliderPrompt, {
      children: prompt
    }), /*#__PURE__*/jsx(TextField, {
      ref: textInput,
      type: "text",
      value: textInputValue,
      "aria-label": `${prompt} text input`,
      onChange: noOp,
      $errorState: errorState
    }), /*#__PURE__*/jsx(Slider, {
      type: "range",
      min: min,
      max: max,
      step: step,
      value: value,
      onChange: handleChange,
      "aria-label": `${prompt} range input`,
      $currentGradientValue: (value - min) / (max - min) * 100,
      $errorState: errorState,
      $sliderThumbSize: 24
    }), /*#__PURE__*/jsxs(SliderLabelsContainer, {
      children: [/*#__PURE__*/jsx(SliderMinLabel, {
        children: minLabel
      }), /*#__PURE__*/jsxs(ErrorMessage, {
        role: 'alert',
        children: [minimumExceeded && /*#__PURE__*/jsx("span", {
          children: "Minimum loan amount is reached"
        }), maximumExceeded && /*#__PURE__*/jsx("span", {
          children: "Maximum loan amount is reached"
        })]
      }), /*#__PURE__*/jsx(SliderMaxLabel, {
        children: maxLabel
      })]
    })]
  });
};
const TextFieldSliderContainer = styled.div`
  flex-direction: column;
  display: flex;
  margin: auto 24px;
  row-gap: 1rem;
`;
const TextFieldSliderPrompt = styled.div`
  color: ${_ref4 => {
  var _theme$colors;
  let {
    theme
  } = _ref4;
  return (theme === null || theme === void 0 ? void 0 : (_theme$colors = theme.colors) === null || _theme$colors === void 0 ? void 0 : _theme$colors.black) || '#242220';
}};
  font-family: mabry-pro-regular, sans-serif;
  font-size: 20px;
`;
const TextField = styled.input`
  height: 69px;
  padding-bottom: 7px;
  margin-bottom: -18px;
  font-family: mabry-pro-regular, sans-serif;
  font-size: 25px;
  text-align: center;
  appearance: none;
  border-color: ${_ref5 => {
  var _theme$colors3;
  let {
    theme,
    $errorState
  } = _ref5;
  if ($errorState) {
    var _theme$colors2;
    return (theme === null || theme === void 0 ? void 0 : (_theme$colors2 = theme.colors) === null || _theme$colors2 === void 0 ? void 0 : _theme$colors2.systemRed) || '#ff4a42';
  }
  return (theme === null || theme === void 0 ? void 0 : (_theme$colors3 = theme.colors) === null || _theme$colors3 === void 0 ? void 0 : _theme$colors3.gray[3]) || '#C9C8C6';
}};
  border-style: solid;
  border-width: ${_ref6 => {
  let {
    $errorState
  } = _ref6;
  if ($errorState) {
    return '2px';
  }
  return 'thin';
}};
  border-radius: 6px 6px 2px 2px;
`;
const Slider = styled.input`
  /* WebKit (Chrome & Safari) */
  /* stylelint-disable property-no-vendor-prefix */

  & {
    height: 2px;
    -webkit-appearance: none;
    ${_ref7 => {
  var _theme$colors5, _theme$colors6, _theme$colors7, _theme$colors8;
  let {
    $currentGradientValue,
    $errorState,
    theme
  } = _ref7;
  if ($errorState) {
    var _theme$colors4;
    return `background: ${(theme === null || theme === void 0 ? void 0 : (_theme$colors4 = theme.colors) === null || _theme$colors4 === void 0 ? void 0 : _theme$colors4.systemRed) || '#ff4a42'};`;
  }
  return `
      background: linear-gradient(to right,
      ${theme === null || theme === void 0 ? void 0 : (_theme$colors5 = theme.colors) === null || _theme$colors5 === void 0 ? void 0 : _theme$colors5.black} 0%,
      ${theme === null || theme === void 0 ? void 0 : (_theme$colors6 = theme.colors) === null || _theme$colors6 === void 0 ? void 0 : _theme$colors6.black} ${$currentGradientValue}%,
      ${theme === null || theme === void 0 ? void 0 : (_theme$colors7 = theme.colors) === null || _theme$colors7 === void 0 ? void 0 : _theme$colors7.gray[3]} ${$currentGradientValue}%,
      ${theme === null || theme === void 0 ? void 0 : (_theme$colors8 = theme.colors) === null || _theme$colors8 === void 0 ? void 0 : _theme$colors8.gray[3]} 100%);
    `;
}}
  }

  &::-webkit-slider-runnable-track {
    height: 2px;
  }

  &::-webkit-slider-thumb {
    width: ${_ref8 => {
  let {
    $sliderThumbSize
  } = _ref8;
  return `${$sliderThumbSize}px`;
}};
    height: ${_ref9 => {
  let {
    $sliderThumbSize
  } = _ref9;
  return `${$sliderThumbSize}px`;
}};
    margin-top: ${_ref10 => {
  let {
    $sliderThumbSize
  } = _ref10;
  return `${-$sliderThumbSize / 2 + 1}px`;
}};
    -webkit-appearance: none;
    background-color: ${_ref11 => {
  var _theme$colors9;
  let {
    theme
  } = _ref11;
  return (theme === null || theme === void 0 ? void 0 : (_theme$colors9 = theme.colors) === null || _theme$colors9 === void 0 ? void 0 : _theme$colors9.black) || '#242220';
}};
    border-radius: 50%;
  }

  /* Firefox */

  &::-moz-range-progress {
    background-color: ${_ref12 => {
  var _theme$colors11;
  let {
    theme,
    $errorState
  } = _ref12;
  if ($errorState) {
    var _theme$colors10;
    return (theme === null || theme === void 0 ? void 0 : (_theme$colors10 = theme.colors) === null || _theme$colors10 === void 0 ? void 0 : _theme$colors10.systemRed) || '#ff4a42';
  }
  return (theme === null || theme === void 0 ? void 0 : (_theme$colors11 = theme.colors) === null || _theme$colors11 === void 0 ? void 0 : _theme$colors11.black) || '#242220';
}};
  }

  &::-moz-range-track {
    background-color: ${_ref13 => {
  var _theme$colors13;
  let {
    theme,
    $errorState
  } = _ref13;
  if ($errorState) {
    var _theme$colors12;
    return (theme === null || theme === void 0 ? void 0 : (_theme$colors12 = theme.colors) === null || _theme$colors12 === void 0 ? void 0 : _theme$colors12.systemRed) || '#ff4a42';
  }
  return (theme === null || theme === void 0 ? void 0 : (_theme$colors13 = theme.colors) === null || _theme$colors13 === void 0 ? void 0 : _theme$colors13.gray[3]) || '#C9C8C6';
}};
  }

  &::-moz-range-thumb {
    width: 24px; /* Set a specific slider handle width */
    height: 24px; /* Slider handle height */
    background-color: ${_ref14 => {
  var _theme$colors14;
  let {
    theme
  } = _ref14;
  return (theme === null || theme === void 0 ? void 0 : (_theme$colors14 = theme.colors) === null || _theme$colors14 === void 0 ? void 0 : _theme$colors14.black) || '#242220';
}};
    border-width: 0;
    border-radius: 50%;
  }
`;
const SliderLabelsContainer = styled.div`
  flex-direction: row;
  display: flex;
`;
const SliderRangeLabel = styled.span`
  flex: 0 1 auto;
  color: ${_ref15 => {
  var _theme$colors15;
  let {
    theme
  } = _ref15;
  return (theme === null || theme === void 0 ? void 0 : (_theme$colors15 = theme.colors) === null || _theme$colors15 === void 0 ? void 0 : _theme$colors15.gray[1]) || '#5A5859';
}};
  font-family: mabry-pro-regular, sans-serif;
  font-size: 16px;
  font-style: normal;
  font-weight: normal;
`;
const SliderMinLabel = styled(SliderRangeLabel)`
  text-align: left;
`;
const SliderMaxLabel = styled(SliderRangeLabel)`
  text-align: right;
`;
const ErrorMessage = styled.div`
  flex: 1 0 auto;
  width: 0;
  padding: 0 1rem;
  color: ${_ref16 => {
  var _theme$colors16;
  let {
    theme
  } = _ref16;
  return (theme === null || theme === void 0 ? void 0 : (_theme$colors16 = theme.colors) === null || _theme$colors16 === void 0 ? void 0 : _theme$colors16.systemRed) || '#ff4a42';
}};
  font-family: inter-regular, sans-serif;
  text-align: center;
`;
TextFieldSlider.propTypes = {
  prompt: PropTypes.string,
  value: PropTypes.number,
  onValueChange: PropTypes.func,
  min: PropTypes.number,
  max: PropTypes.number,
  step: PropTypes.number,
  minLabel: PropTypes.string,
  maxLabel: PropTypes.string,
  id: PropTypes.string,
  onIsValidChange: PropTypes.func,
  showDollarSign: PropTypes.bool
};
TextFieldSlider.defaultProps = {
  prompt: '',
  value: 0,
  onValueChange: () => {},
  min: 1,
  max: 10,
  step: 1,
  minLabel: '',
  maxLabel: '',
  id: undefined,
  onIsValidChange: () => {},
  showDollarSign: false
};
var TextFieldSlider$1 = TextFieldSlider;

export { TextFieldSlider$1 as default };
//# sourceMappingURL=TextFieldSlider.js.map
